import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronRight, RotateCcw } from "lucide-react";

const STAGES = [
  { key: "detect", n: "01", label: "DETECT", module: "Signals", to: "/signals" },
  { key: "validate", n: "02", label: "VALIDATE", module: "Val. Trees", to: "/validation" },
  { key: "contextualize", n: "03", label: "CONTEXTUALIZE", module: "Insights", to: "/insights" },
  { key: "estimate", n: "04", label: "ESTIMATE", module: "Opportunities", to: "/opportunities" },
  { key: "simulate", n: "05", label: "SIMULATE", module: "Sim Runs", to: "/simulate" },
  { key: "decide", n: "06", label: "DECIDE", module: "≥85% Conv.", to: "/whitespace" },
  { key: "develop", n: "07", label: "DEVELOP", module: "Concepts", to: "/concepts" },
  { key: "source", n: "08", label: "SOURCE", module: "Suppliers", to: "/suppliers" },
  { key: "sample", n: "09", label: "SAMPLE", module: "RFPs", to: "/rfps" },
  { key: "launch", n: "10", label: "LAUNCH", module: "Live SKUs", to: "/launches" },
  { key: "learn", n: "11", label: "LEARN", module: "Retrains", to: "/learnings" },
];

function relTime(iso) {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function PredictionLoop() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.loopStats().then(setStats).catch(() => {});
  }, []);

  return (
    <div className="bm-card p-4" data-testid="prediction-loop">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="label-mono text-bm-amber">THE PREDICTION LOOP · LIVE</div>
        <div className="label-mono text-bm-dim">EVERY LAUNCH RETRAINS THE MODEL — THE LOOP COMPOUNDS</div>
      </div>
      <div className="flex items-stretch overflow-x-auto pb-1">
        {STAGES.map((s) => (
          <React.Fragment key={s.key}>
            <button
              onClick={() => navigate(s.to)}
              data-testid={`loop-stage-${s.key}`}
              className="group flex flex-col justify-between min-w-[104px] flex-1 text-left border border-white/10 hover:border-bm-amber/60 bg-bm-elevated/50 px-2.5 py-2 transition-colors"
            >
              <div>
                <div className="label-mono text-bm-dim group-hover:text-bm-amber transition-colors">{s.n}</div>
                <div className="font-mono text-[10px] font-bold tracking-wider text-white mt-0.5">{s.label}</div>
              </div>
              <div className="mt-2">
                <div className="font-heading font-black text-xl text-bm-cyan leading-none">
                  {stats ? stats[s.key] : "·"}
                </div>
                <div className="label-mono text-bm-dim mt-0.5 truncate">{s.module}</div>
              </div>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-bm-dim self-center shrink-0 mx-0.5" />
          </React.Fragment>
        ))}
        {/* Stage 12 — loop closure */}
        <button
          onClick={() => navigate("/ingest")}
          data-testid="loop-stage-detect-again"
          className="group flex flex-col justify-between min-w-[112px] flex-1 text-left border border-dashed border-bm-green/40 hover:border-bm-green bg-bm-green/5 px-2.5 py-2 transition-colors"
        >
          <div>
            <div className="label-mono text-bm-green flex items-center gap-1">
              12 <RotateCcw className="w-3 h-3" />
            </div>
            <div className="font-mono text-[10px] font-bold tracking-wider text-bm-green mt-0.5">DETECT AGAIN</div>
          </div>
          <div className="mt-2">
            <div className="font-heading font-black text-xl text-bm-green leading-none">
              {stats ? relTime(stats.last_crawl) : "·"}
            </div>
            <div className="label-mono text-bm-dim mt-0.5">since crawl</div>
          </div>
        </button>
      </div>
    </div>
  );
}
